import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AtomAvatar } from "@/components/AtomAvatar";
import { fetchUserAvatarConfig, type AvatarState } from "@/lib/avatar";
import { AVATAR_OPTIONS, type AvatarColorOption, type AvatarAccessoryOption } from "@/data/avatarOptions";
import { ELEMENT_LIST, getElementByAtomicNumber } from "@/lib/atomElements";
import { ElementSearchDropdown } from "@/components/ElementSearchDropdown";
import { ArrowLeft, Check, Lock, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HelixLoader } from "@/components/ui/helix-loader";

export default function AvatarCustomizationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Customization state
  const [bodyColor, setBodyColor] = useState("#60A5FA");
  const [elementN, setElementN] = useState(6); // Default Carbon
  const [accessory, setAccessory] = useState("None");

  // Load existing configuration
  useEffect(() => {
    let isMounted = true;
    const loadConfig = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          navigate("/");
          return;
        }

        if (isMounted) {
          setUserId(session.user.id);
        }

        const config = await fetchUserAvatarConfig(session.user.id);
        
        if (isMounted) {
          setBodyColor(config.color);
          setAccessory(config.accessory);
          setElementN(config.elementN || 6);
        }
      } catch (err) {
        console.error("Failed to load user avatar configuration:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadConfig();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);

    const element = getElementByAtomicNumber(elementN);
    const shellsCount = element?.shells?.length || 2;
    const electronsCount = element?.atomicNumber || 6;

    try {
      const { error } = await supabase
        .from("user_avatar_config")
        .upsert({
          user_id: userId,
          body_color: bodyColor,
          glow_color: bodyColor,
          shell_count: shellsCount,
          electron_count: electronsCount,
          accessory_id: accessory,
          updated_at: new Date().toISOString(),
        }, { onConflict: "user_id" });

      if (error) throw error;

      toast.success("Avatar customized and saved successfully!");
      
      // Dispatch custom event to notify other pages
      const event = new CustomEvent("profile-updated", { detail: { userId } });
      window.dispatchEvent(event);
      
    } catch (err) {
      console.error("Failed to save avatar configuration:", err);
      toast.error("Failed to save avatar configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#090D1A] text-white">
        <div className="text-center space-y-4">
          <HelixLoader className="text-primary mx-auto" />
          <p className="text-white/60">Loading Scico customizer...</p>
        </div>
      </div>
    );
  }

  // Construct current preview state
  const previewState: AvatarState = {
    color: bodyColor,
    elementN: elementN,
    accessory: accessory,
  };

  const currentElement = getElementByAtomicNumber(elementN);
  const elementName = currentElement?.name || "Unknown";
  const elementSymbol = currentElement?.symbol || "??";
  const elementShellsCount = currentElement?.shells?.length || 0;

  return (
    <div className="min-h-screen bg-[#090D1A] text-white relative overflow-hidden flex flex-col">
      {/* Background radial glow */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-purple-500/10 blur-[120px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 px-6 py-4 flex items-center justify-between border-b border-white/5 backdrop-blur-md bg-white/[0.02]">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/profile")}
            aria-label="Back to Profile"
            className="rounded-xl border border-white/10 hover:bg-white/5 text-white/70 hover:text-white transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Avatar Customizer
            </h1>
            <p className="text-xs text-white/50">Personalize your unique atomic companion</p>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/25 px-5 py-2 border-0"
        >
          {saving ? (
            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving..." : "Save Selection"}
        </Button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-6xl w-full mx-auto p-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-center relative z-10">
        {/* Left Side: Avatar Live Preview */}
        <div className="col-span-1 md:col-span-5 flex flex-col items-center justify-center p-6 bg-white/[0.03] border border-white/5 rounded-3xl backdrop-blur-lg shadow-2xl relative overflow-hidden h-[420px]">
          {/* Neon preview ring */}
          <div 
            className="absolute inset-0 opacity-10 blur-xl pointer-events-none transition-all duration-700" 
            style={{ 
              background: `radial-gradient(circle, ${bodyColor} 0%, transparent 70%)` 
            }} 
          />
          
          <div className="relative z-10 flex flex-col items-center">
            <div className="relative">
              {/* Spinning decorative border */}
              <div className="absolute inset-[-15px] rounded-full border border-dashed border-white/10 animate-spin-slow pointer-events-none" />
              <AtomAvatar state={previewState} size={200} className="relative z-10" />
            </div>

            <div className="mt-8 text-center">
              <span className="text-xs font-bold tracking-widest text-blue-400 uppercase">Companion Avatar</span>
              <h2 className="text-2xl font-black tracking-tight mt-1">
                {accessory !== "None" ? `${accessory.charAt(0).toUpperCase() + accessory.slice(1)} Atom` : "Atomic Scico"}
              </h2>
              <p className="text-xs text-white/40 mt-1">
                Color: {bodyColor} • Element: {elementName} ({elementSymbol}) • Shells: {elementShellsCount}
              </p>
            </div>
          </div>
        </div>

        {/* Right Side: Options Selector */}
        <div className="col-span-1 md:col-span-7 flex flex-col self-stretch bg-white/[0.02] border border-white/5 rounded-3xl backdrop-blur-lg shadow-2xl overflow-hidden">
          {/* Tabs header */}
          <div className="flex border-b border-white/5 bg-white/[0.01]">
            <button className="flex-1 py-4 text-sm font-bold tracking-wide uppercase border-b-2 border-blue-500 text-white transition-all">
              Customize Companion
            </button>
          </div>

          <div className="p-6 space-y-8 flex-1 overflow-y-auto">
            {/* Options Category 1: Nucleus / Accent Color */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white/60">1. Select Body Accent</h3>
                <span className="text-xs text-blue-400 font-semibold">Glow color matches</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {AVATAR_OPTIONS.colors.map((colorOpt: AvatarColorOption) => {
                  const isSelected = bodyColor === colorOpt.value;
                  const isUnlocked = colorOpt.unlocked;

                  return (
                    <button
                      key={colorOpt.value}
                      disabled={!isUnlocked}
                      onClick={() => setBodyColor(colorOpt.value)}
                      className={`relative aspect-square rounded-2xl flex flex-col items-center justify-center p-2 border transition-all duration-300 ${
                        isSelected 
                          ? "border-blue-500 bg-blue-500/10 shadow-[0_0_15px_rgba(59,130,246,0.2)]" 
                          : "border-white/5 bg-white/[0.02] hover:bg-white/5 hover:border-white/10"
                      } ${!isUnlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div 
                        className="w-8 h-8 rounded-full shadow-inner transition-transform duration-300 group-hover:scale-105" 
                        style={{ backgroundColor: colorOpt.value }}
                      />
                      <span className="text-[10px] mt-1.5 text-center font-medium text-white/70 block truncate w-full">
                        {colorOpt.label}
                      </span>
                      {isSelected && (
                        <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-0.5">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute top-1 right-1 bg-black/60 text-white/60 rounded-full p-0.5">
                          <Lock className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Options Category 2: Element Selection (Orbital Structure) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white/60">2. Orbital Element</h3>
                <span className="text-xs text-white/40">Select atomic structure (all 118 elements)</span>
              </div>
              <div className="flex flex-col gap-3 p-4 border border-white/5 rounded-2xl bg-white/[0.01]">
                <ElementSearchDropdown
                  selectedElementN={elementN}
                  onSelect={(n) => setElementN(n)}
                />
                
                {/* Details badge and buttons */}
                {currentElement && (
                  <div className="flex flex-col sm:flex-row gap-3 items-center justify-between mt-1">
                    <div className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-white/5 px-3.5 py-1.5 font-mono text-[10px] w-full sm:w-auto justify-center sm:justify-start">
                      <span className="text-xs font-bold text-blue-400">{currentElement.symbol}</span>
                      <span className="text-white/20">|</span>
                      <span className="text-white/80">{currentElement.name}</span>
                      <span className="text-white/20">|</span>
                      <span className="text-white/40">No. {currentElement.atomicNumber}</span>
                      <span className="text-white/20">|</span>
                      <span className="text-white/40">Shells: {currentElement.shells.join(', ')}</span>
                    </div>
                    
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setElementN((prev) => Math.max(1, prev - 1))}
                        disabled={elementN <= 1}
                        className="flex-1 sm:flex-none h-8 px-3 border-white/10 hover:bg-white/5 text-white bg-transparent"
                      >
                        -
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setElementN((prev) => Math.min(118, prev + 1))}
                        disabled={elementN >= 118}
                        className="flex-1 sm:flex-none h-8 px-3 border-white/10 hover:bg-white/5 text-white bg-transparent"
                      >
                        +
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Options Category 3: Accessories */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white/60">3. Companion Accessories</h3>
                <span className="text-xs text-white/40">Equip atomic items</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {AVATAR_OPTIONS.accessories.map((accOpt: AvatarAccessoryOption) => {
                  const isSelected = accessory === accOpt.value;
                  const isUnlocked = accOpt.unlocked;

                  return (
                    <button
                      key={accOpt.value}
                      disabled={!isUnlocked}
                      onClick={() => setAccessory(accOpt.value)}
                      className={`relative py-3 px-3 rounded-2xl border text-center transition-all duration-300 flex flex-col justify-center items-center h-20 ${
                        isSelected 
                          ? "border-blue-500 bg-blue-500/10 shadow-[0_0_15px_rgba(59,130,246,0.2)]" 
                          : "border-white/5 bg-white/[0.02] hover:bg-white/5 hover:border-white/10"
                      } ${!isUnlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span className="text-xs font-bold block truncate w-full">{accOpt.label}</span>
                      <span className="text-[9px] text-white/40 mt-1 uppercase tracking-wider">
                        {accOpt.value === "None" ? "Equipped" : "Accessory"}
                      </span>
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 bg-blue-500 text-white rounded-full p-0.5">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute top-1.5 right-1.5 bg-black/60 text-white/60 rounded-full p-0.5">
                          <Lock className="w-2.5 h-2.5" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
